import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Scale, ExternalLink } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import { dec, num, formatNumber } from "@/lib/formatters";
import { tooltipStyle } from "./oversiktConstants";

const g = (v) => (v == null ? "—" : dec(v, 3));
const kr = (v) => (v == null ? "—" : `${formatNumber(v)} kr`);

export const UlikskapCompareChart = ({ data, colors }) => {
  const entries = Object.entries(data?.kommuner || {}).filter(([, k]) => k.gini);
  if (entries.length === 0) return null;
  const land = data.land;
  const giniRows = entries.map(([code, k], i) => ({
    code,
    name: k.name,
    Gini: k.gini?.kommune,
    "P90/P10": k.p90p10?.kommune,
    _barn: k.barnefattigdom?.kommune,
    _barnPeriod: k.barnefattigdom?.period,
    _color: colors?.[i % colors.length] || "#103766",
  }));
  const barnRows = giniRows.filter((r) => r._barn != null);
  const meta = data.budsjett_meta;
  const budsjettRows = entries
    .map(([code, k], i) => ({
      code,
      name: k.name,
      _barn: k.barnefattigdom?.kommune,
      _krInnb: k.budsjett?.kr_per_innb,
      _krBarn: k.budsjett?.kr_per_barn,
      _rankBarn: k.land_rank_barnefattigdom,
      _rankKr: k.land_rank_kr_per_barn,
      _color: colors?.[i % colors.length] || "#103766",
    }))
    .filter((r) => r._krBarn != null);
  const year = entries[0][1].gini?.year;
  const desilYear = entries[0][1].desil?.year;
  const desilRows = Array.from({ length: 10 }, (_, i) => {
    const row = { gruppe: `${i + 1}` };
    entries.forEach(([, k]) => {
      const r = k.desil?.rows?.[i];
      if (r) row[k.name] = r.andel_pct;
    });
    return row;
  });
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="compare-ulikskap-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Scale className="w-5 h-5 text-[#103766]" />
          Inntektsulikskap {year ? `(${year})` : ""}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Gini-koeffisient og P90/P10 for inntekt etter skatt. Lågare Gini tyder jamnare fordeling.
          {land ? ` Landet: Gini ${g(land.gini)} · P90/P10 ${g(land.p90p10)}.` : ""}
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Gini-koeffisient</p>
          <div className="h-[170px]" data-testid="compare-gini-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={giniRows} layout="vertical" margin={{ left: 8, right: 40 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis type="number" stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => num(v, 3)} />
                <YAxis type="category" dataKey="name" width={110} stroke="#8AA4BF" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => g(v)} />
                <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                {land?.gini != null && (
                  <ReferenceLine x={land.gini} stroke="#C0392B" strokeDasharray="4 3" label={{ value: "Landet", position: "top", fontSize: 10, fill: "#C0392B" }} />
                )}
                <Bar dataKey="Gini" radius={[0, 4, 4, 0]} barSize={18}>
                  {giniRows.map((r) => (
                    <Cell key={r.code} fill={r._color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#4B647F] mt-1">
            {giniRows.map((r) => (
              <span key={r.code} data-testid={`compare-gini-${r.code}`}>
                <span className="font-medium text-[#1F2A36]">{r.name}</span>: Gini {g(r.Gini)} · P90/P10 {g(r["P90/P10"])}
                {r._barn != null ? ` · ${dec(r._barn, 1)} % barn i vedvarande låginntekt` : ""}
              </span>
            ))}
          </div>
        </div>
        {barnRows.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
              Barn i hushald med vedvarande låginntekt ({barnRows[0]._barnPeriod})
            </p>
            <div className="h-[170px]" data-testid="compare-barnefattigdom-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barnRows} layout="vertical" margin={{ left: 8, right: 40 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis type="number" stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => `${num(v)} %`} />
                  <YAxis type="category" dataKey="name" width={110} stroke="#8AA4BF" fontSize={11} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${dec(v, 1)} %`} />
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="_barn" name="Barn i vedvarande låginntekt" radius={[0, 4, 4, 0]} barSize={18}>
                    {barnRows.map((r) => (
                      <Cell key={r.code} fill={r._color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {budsjettRows.length > 0 && (
          <div data-testid="compare-barn-budsjett">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
              Pengebruk på barn og unge {meta?.year} – per barn 0–17 år
            </p>
            <p className="text-xs text-[#8AA4BF] mb-2">
              Netto driftsutgifter til barnehage, grunnskule og barnevern delt på talet på barn i kommunen, så små og store kommunar kan
              samanliknast.{" "}
              {meta?.land_snitt_kr_barn ? `Landssnittet er ${kr(meta.land_snitt_kr_barn)} per barn.` : ""}
            </p>
            <div className="h-[170px]" data-testid="compare-kr-per-barn-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budsjettRows} layout="vertical" margin={{ left: 8, right: 60 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis
                    type="number" stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => formatNumber(v)}
                    domain={[0, (dataMax) => Math.round(Math.max(dataMax, meta?.land_snitt_kr_barn || 0) * 1.08)]}
                  />
                  <YAxis type="category" dataKey="name" width={110} stroke="#8AA4BF" fontSize={11} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => kr(v)} />
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                  {meta?.land_snitt_kr_barn != null && (
                    <ReferenceLine x={meta.land_snitt_kr_barn} stroke="#C0392B" strokeDasharray="4 3" label={{ value: "Landssnitt", position: "top", fontSize: 10, fill: "#C0392B" }} />
                  )}
                  <Bar dataKey="_krBarn" name="Kroner per barn 0–17 år" radius={[0, 4, 4, 0]} barSize={18}>
                    {budsjettRows.map((r) => (
                      <Cell key={r.code} fill={r._color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs" data-testid="compare-barn-budsjett-table">
                <thead>
                  <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                    <th className="py-2 pr-3 text-left font-medium">Kommune</th>
                    <th className="py-2 px-3 text-right font-medium">Barnefattigdom</th>
                    <th className="py-2 px-3 text-right font-medium">Plass i landet</th>
                    <th className="py-2 px-3 text-right font-medium">Per innbyggjar</th>
                    <th className="py-2 px-3 text-right font-medium">Per barn 0–17 år</th>
                    <th className="py-2 pl-3 text-right font-medium">Plass i landet</th>
                  </tr>
                </thead>
                <tbody>
                  {budsjettRows.map((r) => (
                    <tr key={r.code} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`compare-barn-budsjett-${r.code}`}>
                      <td className="py-2 pr-3 font-medium" style={{ color: r._color }}>{r.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r._barn == null ? "—" : `${dec(r._barn, 1)} %`}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-[#4B647F]">
                        {r._rankBarn?.own_rank ? `${r._rankBarn.own_rank}. av ${r._rankBarn.count}` : "—"}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{kr(r._krInnb)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{kr(r._krBarn)}</td>
                      <td className="py-2 pl-3 text-right tabular-nums text-[#4B647F]">
                        {r._rankKr?.own_rank ? `${r._rankKr.own_rank}. av ${r._rankKr.count}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {desilYear && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
              Inntektsgrupper {desilYear} – andel av hushalda i kvar tidels-gruppe i landet
            </p>
            <div className="h-[260px]" data-testid="compare-desil-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={desilRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="gruppe" stroke="#8AA4BF" fontSize={11} label={{ value: "Inntektsgruppe (1 = lågast)", position: "insideBottom", offset: -4, fontSize: 10, fill: "#8AA4BF" }} />
                  <YAxis stroke="#8AA4BF" fontSize={11} width={45} tickFormatter={(v) => `${num(v)} %`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${dec(v, 1)} %`} />
                  <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={10} stroke="#C0392B" strokeDasharray="4 3" label={{ value: "10 % = landssnitt", position: "right", fontSize: 10, fill: "#C0392B" }} />
                  {entries.map(([code, k], i) => (
                    <Line key={code} type="monotone" dataKey={k.name} stroke={colors?.[i % colors.length] || "#103766"} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/09114" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            09114 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          og{" "}
          <a href="https://www.ssb.no/statbank/table/12558" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12558 <ExternalLink className="w-3 h-3" />
          </a>
          , barnefattigdom frå{" "}
          <a href="https://www.ssb.no/statbank/table/12944" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12944 <ExternalLink className="w-3 h-3" />
          </a>{" "}
          og pengebruk frå KOSTRA{" "}
          <a href="https://www.ssb.no/statbank/table/12362" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
            12362 <ExternalLink className="w-3 h-3" />
          </a>.
        </p>
      </CardContent>
    </Card>
  );
};

export default UlikskapCompareChart;
